import { useQuery, useReactiveVar } from '@apollo/client'
import { groupBy } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactPixel from 'react-facebook-pixel'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'

import { Helmet } from '@src/components/Helmet'
import {
  MenuItemGroupFulfillmentMethod,
  NarrowFulfilmentMethodInputType,
  OutletMenuItemGroup,
} from '@src/graphql-types'
import { CurrentFulfilmentType } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/validation'
import { useAllDiscountsQuery } from '@src/hooks/sharedQueries/useAllDiscounts/useAllDiscounts'
import { useLazyTablesQuery } from '@src/hooks/sharedQueries/useTables/useTables'
import { useCheckoutRouter } from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { useShowInactiveQueryParam } from '@src/hooks/useShowInactiveQueryParam'
import { useShowOutletPreviewParam } from '@src/hooks/useShowOutletPreviewParam'
import { useBreakpoint } from '@src/hooks/useBreakpoint'

import { ChosenFulfilmentToMenuItemGroupFulfillmentMethod } from '@src/components/CheckoutModal/Checkout/AddOnItems/AddOnItems'
import { showAppBannerVar } from '@src/components/SiteHeader/utils/reactiveVar'
import { useCustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import { jwtVar } from '../../models/customer/jwt'
import { OutletBottomSheet } from './OutletBottomSheet/OutletBottomSheet'
import { OutletHeader } from './OutletHeader/OutletHeader'
import { OutletMenu } from './OutletMenu/OutletMenu'
import { GetOutletMenuDocument } from './OutletMenu/queries/__generated__/GetOutletMenu.graphql-interface'
import { OutletMenuSection } from './OutletMenuSections/OutletMenuSection'
import { OutletOverview } from './OutletOverview/OutletOverview'
import {
  MenuWrapper,
  ObservableDiv,
} from './OutletOverview/OutletOverview.styles'
import { HeaderWrapper, StickyHeader } from './OutletPage.styles'
import { OutletPageProvider, useOutletPageContext } from './OutletPageContext'
import { OutletPageSkeleton } from './OutletPageSkeleton/OutletPageSkeleton'
import { OutletPreviewSelectModal } from './OutletPreviewSelect/OutletPreviewSelectModal'
import { OutletStackedMenu } from './OutletStackedMenu/OutletStackedMenu'
import { OutletStackedMenuView } from './OutletStackedMenu/OutletStackedMenuView'
import { getAddOnItemsForOutletDocument } from './queries/__generated__/getAddOnItemsForOutlet.graphql-interface'

export const ONE_MINUTE = 60 * 1000

export const MAPPED_FULFILMENT_METHODS: Record<
  NarrowFulfilmentMethodInputType,
  MenuItemGroupFulfillmentMethod
> = {
  [NarrowFulfilmentMethodInputType.DELIVERY]:
    MenuItemGroupFulfillmentMethod.DELIVERY,
  [NarrowFulfilmentMethodInputType.COLLECTION]:
    MenuItemGroupFulfillmentMethod.COLLECTION,
  [NarrowFulfilmentMethodInputType.TABLE]: MenuItemGroupFulfillmentMethod.TABLE,
}

const OutletPageContent: React.FC = () => {
  const {
    headerHeight,
    isStackedMenu,
    treeForModalData,
    setTreeForModalData,
    activeMenuId,
    setHeaderHeight,
    setIsCollapsingDivVisible,
    outletFulfilment,
    showMenuSearchResults,
  } = useOutletPageContext()

  const { isMobile } = useBreakpoint()

  const {
    data: { currentFulfilment },
    outlet,
    setCurrentFulfilment,
  } = outletFulfilment

  const { ref: expandHeaderRef, inView } = useInView({
    root: null,
    rootMargin: '-80px 0px 0px 0px',
    threshold: [0, 1],
  })

  useEffect(() => {
    // Keep overview collapsed when showing search results on mobile
    if (showMenuSearchResults && isMobile) {
      setIsCollapsingDivVisible(true)
    } else {
      setIsCollapsingDivVisible(!inView)
    }
  }, [inView, setIsCollapsingDivVisible, showMenuSearchResults, isMobile])

  const headerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!headerRef.current) return

    const updateHeight = () => {
      if (headerRef.current && inView) {
        const height = headerRef.current.offsetHeight
        if (height > 0) {
          setHeaderHeight(height)
        }
      }
    }

    // Initial
    updateHeight()

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(headerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [inView, setHeaderHeight])

  // every minute update the availability (by updating the state, which triggers a rerender)
  const [menuItemGroupTreeLastUpdated, setMenuItemGroupTreeLastUpdated] =
    useState(new Date())
  useEffect(() => {
    const interval = setInterval(() => {
      setMenuItemGroupTreeLastUpdated(new Date())
    }, ONE_MINUTE)
    return () => clearInterval(interval)
  }, [])

  const { pathname } = useLocation()
  const [showInactive] = useShowInactiveQueryParam()
  const [outletPreview] = useShowOutletPreviewParam()
  const showAppBanner = useReactiveVar(showAppBannerVar)
  const checkoutRouter = useCheckoutRouter()
  const { t } = useTranslation('outletOverview')

  const [tableIdQueryParam, setTableIdQueryParam] = useQueryParam(
    'table',
    StringParam
  )
  const [tablesQuery] = useLazyTablesQuery({
    outletId: outlet.id,
  })

  const isMobileStackedMenuItemsPageView =
    pathname.includes('/menu/') &&
    (pathname.split('/menu/')[1]?.length || 0) > 0

  const { displayPermanentCheckout } = useCheckoutRouter()

  // fire pixel ViewContent event when outlet page is loaded
  useEffect(() => {
    if (outlet.id) {
      ReactPixel.track('ViewContent', {
        content_ids: outlet.id,
        content_type: 'outlet',
        content_name: outlet.displayName,
      })
    }
  }, [outlet.displayName, outlet.id])

  useEffect(() => {
    if (tableIdQueryParam) {
      void tablesQuery({
        onCompleted: ({ tables }) => {
          if (tables.some(table => table.id === tableIdQueryParam)) {
            setCurrentFulfilment({
              type: CurrentFulfilmentType.TABLE,
              tableId: tableIdQueryParam,
            })
          } else {
            setCurrentFulfilment({
              type: CurrentFulfilmentType.TABLE_UNSELECTED,
            })
          }
          setTableIdQueryParam(undefined)
        },
      })
    }
  }, [
    setCurrentFulfilment,
    setTableIdQueryParam,
    tableIdQueryParam,
    tablesQuery,
  ])

  // get menuItemGroups and skip if stacked menu
  const {
    data: menuItemGroupsQueryResponse,
    previousData,
    loading: menuLoading,
  } = useQuery(GetOutletMenuDocument, {
    variables: {
      outletId: outlet.id,
      endOfPrep: showInactive
        ? undefined
        : currentFulfilment.endOfPrep?.toISOString(),
      showInactive,
      fulfilmentMethods:
        MAPPED_FULFILMENT_METHODS[currentFulfilment.narrowType],
    },
    skip: isStackedMenu,
  })

  const unfilteredMenuItemGroups: OutletMenuItemGroup[] = useMemo(
    () =>
      ((menuItemGroupsQueryResponse || previousData)
        ?.menuItemGroupsForOutlet as OutletMenuItemGroup[]) ?? [],
    [menuItemGroupsQueryResponse, previousData, currentFulfilment.narrowType]
  )

  const menuItemGroups = useMemo(
    () =>
      unfilteredMenuItemGroups
        ? unfilteredMenuItemGroups.filter(
            menuItemGroup =>
              (showInactive ||
                !outlet.hiddenMenuItemGroupIds.includes(menuItemGroup.id)) &&
              menuItemGroup.narrowFulfilmentMethods.includes(
                currentFulfilment.narrowType
              ) &&
              !menuItemGroup.addOnItemsMenu
          )
        : [],
    [
      unfilteredMenuItemGroups,
      showInactive,
      currentFulfilment.narrowType,
      outlet.hiddenMenuItemGroupIds,
    ]
  )

  const processMenuItemGroups = (menuItemGroups: OutletMenuItemGroup[]) => {
    if (!menuItemGroups) {
      return []
    }

    // Filter out menu items that have add-on items
    const menuItemsWithoutAddonItems = menuItemGroups.filter(
      ({ addOnItemsMenu }) => !addOnItemsMenu
    )

    // Group items into parent menus and submenus
    const { parentMenus = [], subMenus = [] } = groupBy(
      menuItemsWithoutAddonItems,
      menuItemGroup => (menuItemGroup.parentMenu ? 'subMenus' : 'parentMenus')
    )

    // Nest submenus under parent menus
    return parentMenus.map(parentMenu => {
      const subMenusForParentMenu = subMenus.filter(
        subMenu => subMenu.parentMenu?.id === parentMenu.id
      )
      return {
        parentMenu,
        subMenus: subMenusForParentMenu,
      }
    })
  }

  // Process menu item groups with useMemo to avoid unnecessary recalculations
  const processedMenuItemTree = useMemo(
    () =>
      !unfilteredMenuItemGroups || unfilteredMenuItemGroups.length === 0
        ? []
        : processMenuItemGroups(unfilteredMenuItemGroups),
    [
      unfilteredMenuItemGroups,
      menuItemGroupTreeLastUpdated,
      showInactive,
      currentFulfilment.narrowType,
    ]
  )

  useEffect(() => {
    if (isStackedMenu) return
    // Only update if we have data and either:
    // 1. We haven't set the initial data yet, or
    // 2. The processed tree has meaningfully changed, or
    // 3. The menuItemGroupTreeLastUpdated has changed (for periodic updates)
    if (
      processedMenuItemTree.length > 0 &&
      (!treeForModalData.length ||
        JSON.stringify(processedMenuItemTree) !==
          JSON.stringify(treeForModalData))
    )
      setTreeForModalData(processedMenuItemTree)
  }, [
    processedMenuItemTree,
    setTreeForModalData,
    treeForModalData,
    isStackedMenu,
  ])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { data: discountsData, loading: discountsLoading } =
    useAllDiscountsQuery({
      outletId: outlet.id,
      fulfilmentNarrowType: currentFulfilment.narrowType,
    })

  const isAuthenticated = useReactiveVar(jwtVar) !== null
  const { data: customerDetailsData, loading: customerDetailsLoading } =
    useCustomerDetailsAndAddressesQuery({
      addressAcceptingOrdersToOutletId: outlet.id,
      queryOptions: {
        skip: !isAuthenticated,
      },
    })

  const { data: addOnItemsData, loading: addOnItemsLoading } = useQuery(
    getAddOnItemsForOutletDocument,
    {
      variables: {
        outletId: outlet.id,
        fulfilmentMethods: [
          ChosenFulfilmentToMenuItemGroupFulfillmentMethod[
            outletFulfilment.data.currentFulfilment.narrowType
          ],
        ],
      },
    }
  )

  const dealsAndOffersMenuItem = {
    parentMenu: {
      id: 'discountMenuItem',
      name: t('deals_and_offers'),
    },
  }

  if (
    (!menuItemGroups && !isStackedMenu) ||
    discountsLoading ||
    (menuLoading && !treeForModalData.length) ||
    addOnItemsLoading
  )
    return <OutletPageSkeleton />

  const hasDealsAndOffers =
    (discountsData?.allDiscountsByOutletId?.length ?? 0) > 0 ||
    (customerDetailsData?.customerDetails?.loyaltyCards?.length ?? 0) > 0 ||
    (outlet?.outletLoyaltyCards?.length ?? 0) > 0 ||
    (addOnItemsData?.addOnMenuItems?.length ?? 0) > 0

  const updatedAvailableMenuItemGroupTree = hasDealsAndOffers
    ? [dealsAndOffersMenuItem, ...treeForModalData]
    : [...treeForModalData]

  const defaultedActiveMenuId =
    activeMenuId ||
    (hasDealsAndOffers
      ? 'discountMenuItem'
      : treeForModalData?.[0]?.parentMenu?.id) ||
    null

  return (
    <>
      <Helmet
        title={outlet.displayName}
        description={outlet.restaurant.metaDescription}
        keywords={outlet.restaurant.metaKeywords}
      />

      <StickyHeader ref={headerRef} showAppBanner={showAppBanner}>
        <HeaderWrapper
          permanentCheckoutOpen={displayPermanentCheckout}
          checkoutOpen={checkoutRouter.route !== undefined}
        >
          <OutletOverview
            permanentCheckout={displayPermanentCheckout}
            isInStackedMenuView={isMobileStackedMenuItemsPageView}
            outletId={outlet.id}
            outletDisplayName={outlet.displayName}
          />
          <OutletHeader />
          <OutletMenuSection
            parentMenus={updatedAvailableMenuItemGroupTree.map(
              ({ parentMenu }) => parentMenu
            )}
            activeMenuId={defaultedActiveMenuId}
            outletId={outlet.id}
            outletDisplayName={outlet.displayName}
            isInStackedMenuView={isMobileStackedMenuItemsPageView}
          />
        </HeaderWrapper>
      </StickyHeader>

      <Routes>
        <Route
          path={`menu/:menuGroupId`}
          element={
            <MenuWrapper headerHeight={headerHeight}>
              <ObservableDiv ref={expandHeaderRef} aria-hidden />
              <OutletStackedMenuView />
            </MenuWrapper>
          }
        />

        <Route
          path={`/`}
          element={<Navigate to={`${pathname}/menu`} replace />}
        />
        <Route
          path={'menu'}
          element={
            <MenuWrapper headerHeight={headerHeight}>
              {outletPreview && (
                <OutletPreviewSelectModal
                  showModal={outletPreview}
                  outletId={outlet.id}
                />
              )}
              <ObservableDiv ref={expandHeaderRef} aria-hidden />
              {!isStackedMenu ? (
                <OutletMenu
                  allDiscounts={discountsData}
                  discountsLoading={discountsLoading}
                  customerDetails={customerDetailsData}
                  customerDetailsLoading={customerDetailsLoading}
                  addOnItemsLoading={addOnItemsLoading}
                  addOnItems={addOnItemsData}
                />
              ) : (
                <OutletStackedMenu allDiscounts={discountsData} />
              )}
            </MenuWrapper>
          }
        />
        <Route
          path={`${pathname}/info`}
          element={
            <Route
              path={`${pathname}/info`}
              element={
                <Navigate to={`${pathname}/menu?showDetails=1`} replace />
              }
            />
          }
        />
      </Routes>

      <OutletBottomSheet outletId={outlet.id} />
    </>
  )
}

export const OutletPage: React.FC = () => (
  <OutletPageProvider>
    <OutletPageContent />
  </OutletPageProvider>
)
